import React, { useCallback, useContext, useEffect, useState } from 'react'

import { Button } from 'core-components/library/button'
import { LoaderOverlay } from 'core-components/library/loader'
import Logger from 'core/utils/Logger'
import {
  onboardingBPTCReconfirmationApi,
  TBPTCReconfirmationPartner,
} from 'onboarding/classes/services/api/OnboardingBPTCReconfirmationApi'
import styles from 'onboarding/components/page/bpTCReconfirmation/Body.module.scss'
import { AgreementSection } from 'onboarding/components/page/bpTCReconfirmation/elements/AgreementSection'
import { ConfirmationBanner } from 'onboarding/components/page/bpTCReconfirmation/elements/ConfirmationBanner'
import { BPTCReconfirmationPartnerDataSection } from 'onboarding/components/page/bpTCReconfirmation/elements/PartnerDataSection'
import { PermissionsSection } from 'onboarding/components/page/bpTCReconfirmation/elements/PermissionsSection'
import { TermsDocument } from 'onboarding/components/page/bpTCReconfirmation/elements/TermsDocument'
import { BPRegistrationTranslationCodes } from 'onboarding/configs/translations/bpRegistrationTranslationCodes'
import { LanguageContext } from 'onboarding/context/LanguageContext'

type FormState = {
  allowEmail: boolean
  allowCall: boolean
  isAgreed: boolean
  isItallyAdditionAgreed: boolean
}

export const BPTCReconfirmationBody: React.FC = () => {
  const { language, t } = useContext(LanguageContext)
  const [isLoading, setIsLoading] = useState(false)

  const [sending, setSending] = useState(false)
  const [error, setError] = useState('')
  const [disabled, setDisabled] = useState(false)
  const [templateUID, setTemplateUID] = useState<string | null>(null)
  const [attachmentUID, setAttachmentUID] = useState<string | null>(null)
  const [partner, setPartner] = useState<TBPTCReconfirmationPartner | null>(null)
  const [form, setForm] = useState<FormState>({
    allowEmail: true,
    allowCall: true,
    isAgreed: false,
    isItallyAdditionAgreed: false,
  })
  const [reconfirmationSignDate, setReconfirmationSignDate] = useState<string | Date | null>(null)
  const [successMessage, setSuccessMessage] = useState('')
  const [translationTexts, setTranslationTexts] = useState({
    agreementMainText: '',
    agreementConfirmationText: '',
    partnerDataPartnerIdText: '',
    partnerDataCompanyText: '',
    partnerDataVatIDText: '',
  })

  const italyChecked = useCallback(() => {
    let result = true

    if (isItalyPartner(partner) && !form.isItallyAdditionAgreed) {
      result = false
    }

    return result
  }, [form.isItallyAdditionAgreed, partner])

  const canSave = form.isAgreed && italyChecked() && templateUID && partner

  useEffect(() => {
    const fetch = async () => {
      setError('')
      setSuccessMessage('')
      const result = await onboardingBPTCReconfirmationApi.fetch(language)
      if (result.status === 'success') {
        const { reconfirmation, partner } = result.data
        setTemplateUID(result.data.templateUID)
        setAttachmentUID(result.data.attachmentUID)
        if (reconfirmation) {
          setForm({
            allowEmail: reconfirmation.allowEmail,
            allowCall: reconfirmation.allowCall,
            isAgreed: reconfirmation.signDate ? true : false,
            isItallyAdditionAgreed: reconfirmation.signDate ? true : false,
          })
          setReconfirmationSignDate(reconfirmation.signDate as unknown as string)
          setDisabled(true)
        } else {
          setReconfirmationSignDate(null)
          setDisabled(false)
        }
        setPartner(partner)
      } else {
        if (result?.message) {
          setError(result.message)
        }

        if (result.data?.messages) {
          setError(result.data.messages.join('; '))
        }
      }
    }

    setIsLoading(true)
    fetch()
      .catch(Logger.error)
      .finally(() => setIsLoading(false))
  }, [language])

  useEffect(() => {
    setTranslationTexts({
      partnerDataPartnerIdText: t(BPRegistrationTranslationCodes.partner_id),
      partnerDataCompanyText: t(BPRegistrationTranslationCodes.company),
      partnerDataVatIDText: t(BPRegistrationTranslationCodes.vat_id),
      agreementMainText: t(BPRegistrationTranslationCodes.step4_check),
      agreementConfirmationText: t(
        BPRegistrationTranslationCodes.step4_confimation.replace(`{'&'}`, '&')
      ),
    })
  }, [language, t])

  const onSubmit = async () => {
    setSending(true)
    setDisabled(true)
    setSuccessMessage('')
    if (templateUID !== null) {
      const result = await onboardingBPTCReconfirmationApi.submit({
        templateUID,
        allowEmail: form.allowEmail,
        allowCall: form.allowCall,
        isAgreed: form.isAgreed,
      })

      if (result.status === 'success') {
        setDisabled(true)
        setSuccessMessage('Form is successfully submitted.')
      } else {
        setDisabled(false)
        setError(result?.message || 'Something went wrong.')
      }
    }
    setSending(false)
  }

  const formatDateTime = (d: string | Date) => {
    try {
      const date = typeof d === 'string' ? new Date(d) : d
      if (Number.isNaN(date.getTime())) return String(d)
      return date.toLocaleString()
    } catch {
      return String(d)
    }
  }

  if (isLoading) {
    return (
      <>
        <LoaderOverlay show={isLoading} />
      </>
    )
  }

  if (error) {
    return (
      <>
        <div className={styles.errorBox}>
          <strong>{error}</strong>
        </div>
      </>
    )
  }

  return (
    <>
      <BPTCReconfirmationPartnerDataSection
        partner={partner}
        texts={{
          partnerIdText: translationTexts.partnerDataVatIDText,
          companyText: translationTexts.partnerDataCompanyText,
          vatIDText: translationTexts.partnerDataVatIDText,
        }}
      />
      <ConfirmationBanner date={reconfirmationSignDate} formatDateTime={formatDateTime} />
      <TermsDocument attachmentUID={attachmentUID} />

      <br />

      <div className={styles.container}>
        <PermissionsSection
          disabled={disabled}
          allowEmail={form.allowEmail}
          allowCall={form.allowCall}
          onChangeEmail={(val) => setForm((prev) => ({ ...prev, allowEmail: val }))}
          onChangeCall={(val) => setForm((prev) => ({ ...prev, allowCall: val }))}
        />

        <AgreementSection
          disabled={disabled}
          isAgreed={form.isAgreed}
          onToggleAgreed={() => setForm((prev) => ({ ...prev, isAgreed: !prev.isAgreed }))}
          showItalyAddition={isItalyPartner(partner)}
          isItalyAgreed={form.isItallyAdditionAgreed}
          onToggleItalyAgreed={() =>
            setForm((prev) => ({ ...prev, isItallyAdditionAgreed: !prev.isItallyAdditionAgreed }))
          }
          texts={{
            mainText: translationTexts.agreementMainText,
            confirmationText: translationTexts.agreementConfirmationText,
          }}
        />

        <div className={styles.submitBlock}>
          <Button disabled={!canSave || disabled} onClick={onSubmit} loading={sending}>
            Submit
          </Button>
          {successMessage && <div className={styles.success}>{successMessage} </div>}
        </div>
      </div>
    </>
  )
}

const isItalyPartner = (partner: TBPTCReconfirmationPartner | null) => {
  let result = false
  if (partner && partner?.address.countryISO.toUpperCase() === 'IT') {
    result = true
  }

  return result
}
